// React component that renders a single stop info
import React, { ReactElement, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import TwExpandableText from '../../../TwExpandableText/TwExpandableText';
import LocationInfo from './LocationInfo';
import ScheduleInfo from './ScheduleInfo';
import LocationContactInfo from './LocationContactInfo';
import { useMediaQuery, Theme } from '@mui/material';
import { StopStatusUpdateInputProps } from '../StatusUpdateInput/StopStatusUpdateInput';
import { TwButton, TwButtonType } from '../../../TwButtons/TwButtons';
import AddSignature from '../../../../pages/PublicLoad/AddSignature/AddSignature';
import useFeatureFlags from '../../../../providers/FeatureFlags/FeatureFlags.provider';
import { StopStatus, Trip, TripStop } from '../../../../redux/api/trips/trips.model';
import {
  TwLoadTooltipInfoProps,
  TwTripTooltipInfoProps
} from '../../../TwLoadTooltipInfo/TwLoadTooltipInfo';

export interface StopInfoProps {
  /**
   * Previous stop
   */
  prevStop?: TripStop;
  /**
   * stop
   * */
  stop: TripStop;
  /**
   * Next stop
   */
  nextStop?: TripStop;
  /**
   * Load id
   */
  loadId?: string;
  /**
   * React Element that handles any action on the stop
   */
  stopActionElement?: ReactElement<StopStatusUpdateInputProps>;
  /**
   * boolean to show and restrict actions to customers
   */
  isCustomerLink: boolean;
  /**
   * Next stop status
   */
  nextStopStatus?: StopStatus;
  /**
   * boolean to show/hide the add signature action button
   */
  showAddSignatureAction?: boolean;
  /**
   * boolean to show/hide the check in and out times
   */
  showCheckInAndOutTimes?: boolean;
  /**
   * Public load token
   */
  publicLoadToken?: string;
  /**
   * callback called when signature is saved
   */
  onSignatureSaved?: () => void;
  /**
   * LTL order readable id for stop (only for LTL)
   */
  LTLOrderReadableId?: string | number;
  /**
   * LTL trip readable id for stop (only for LTL)
   */
  LTLTripReadableId?: string | number;
  /**
   * Orders tooltip data
   */
  ordersTooltipDataArray?: TwLoadTooltipInfoProps[];
  /**
   * Trips tooltip data
   */
  tripsTooltipDataArray?: TwTripTooltipInfoProps[];
}

const StopNotes = ({ notes }: { notes?: string | null }): JSX.Element => {
  if (!notes) return <></>;

  return (
    <Typography variant="body2" fontWeight="400" color="#1F2937" fontSize="14px">
      <TwExpandableText text={notes} characterLimit={140} />
    </Typography>
  );
};

const StopInfo: React.FC<StopInfoProps> = ({
  prevStop,
  stop,
  nextStop,
  loadId,
  nextStopStatus,
  stopActionElement,
  showAddSignatureAction,
  showCheckInAndOutTimes,
  isCustomerLink,
  LTLOrderReadableId,
  LTLTripReadableId,
  publicLoadToken,
  ordersTooltipDataArray,
  tripsTooltipDataArray,
  onSignatureSaved
}): JSX.Element => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signatureViewOnlyMode, setSignatureViewOnlyMode] = useState(false);

  const { enableStopSignature } = useFeatureFlags();
  /**
   * Sets the signature view only mode to true and opens the signature dialog
   */
  const handleViewSignature = (): void => {
    setSignatureViewOnlyMode(true);
    setSignatureDialogOpen(true);
  };

  /**
   * Sets the signature view only mode to true if a signature was submitted
   */
  const handleSignatureSaved = (): void => {
    setSignatureViewOnlyMode(true);
    onSignatureSaved && onSignatureSaved();
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      justifyContent="space-between"
      sx={{ width: '100%', marginBottom: '22px' }}
    >
      <Stack direction="column" spacing={1} sx={{ width: '100%' }} justifyContent="space-between">
        <Stack direction="column" spacing={0} sx={{ width: '100%' }} justifyContent="space-between">
          <LocationInfo
            location={stop?.location}
            isCustomerLink={isCustomerLink}
            status={stop.status}
            nextStopStatus={nextStopStatus}
            ordersTooltipDataArray={ordersTooltipDataArray}
            tripsTooltipDataArray={tripsTooltipDataArray}
            LTLOrderReadableId={LTLOrderReadableId}
            LTLTripReadableId={LTLTripReadableId}
            hasSignature={
              enableStopSignature &&
              (stop?.stop_signature_image_uploaded || stop.load_stop_signature) &&
              showAddSignatureAction
            }
            onViewSignature={handleViewSignature}
          />
          {isMobile && stopActionElement && stopActionElement}
          {enableStopSignature &&
            !stop?.stop_signature_image_uploaded &&
            !stop.load_stop_signature &&
            showAddSignatureAction && (
              <TwButton
                sx={{ margin: '8px 0 0 0', maxWidth: 'fit-content' }}
                label="Add signature"
                action={(): void => setSignatureDialogOpen(true)}
                type={TwButtonType.secondary}
              />
            )}
        </Stack>

        <ScheduleInfo
          prevStop={prevStop}
          stop={stop}
          nextStop={nextStop}
          isCustomerLink={isCustomerLink}
          showInAndOutTimes={showCheckInAndOutTimes}
        />
        {!isCustomerLink && <StopNotes notes={stop?.notes} />}
        <LocationContactInfo stop={stop} />
      </Stack>
      {!isMobile && stopActionElement && stopActionElement}
      <AddSignature
        open={signatureDialogOpen}
        onSignatureSaved={handleSignatureSaved}
        onClose={(): void => {
          setSignatureDialogOpen(false);
          setSignatureViewOnlyMode(false);
        }}
        viewOnlyMode={signatureViewOnlyMode}
        publicLoadToken={publicLoadToken}
        loadId={loadId}
        stop={stop}
      />
    </Stack>
  );
};

export default StopInfo;
